import {
  Microscope,
  Presentation,
  Route,
  Scroll,
  TextSearch,
} from 'lucide-react';

import type { ToolId } from '@/types/tools';

import type { Shortcut } from './shortcutsConfig';

export const TOOL_SHORTCUTS: Partial<Record<ToolId, Shortcut[]>> = {
  'customer-journey-map': [
    {
      id: 'pitch-story',
      order: 1,
      icon: Presentation,
      label: 'Summarize this customer journey in a short narrative',
      prompt:
        'Condense this customer journey into a short, compelling narrative I can share with stakeholders \u2014 highlight the key moments and pain points.',
      condition: 'populated',
    },
  ],
  'business-model-canvas': [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Identify gaps in my business model',
      prompt:
        'Scan this Business Model Canvas for empty, thin, or underdeveloped sections and suggest what to add to each one.',
      condition: 'populated',
    },
  ],
  'lean-canvas': [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Identify gaps in the product idea mapped to this canvas',
      prompt:
        'Scan this Lean Canvas for empty, thin, or underdeveloped sections and suggest what to add to strengthen each one.',
      condition: 'populated',
    },
  ],
  'customer-profile': [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Surface inconsistencies in this customer profile',
      prompt:
        'Check all three sections of this Customer Profile \u2014 flag any missing types like emotional jobs, social jobs, or unexpected gains, and suggest specific items to add.',
      condition: 'populated',
    },
    {
      id: 'interview-questions',
      order: 2,
      icon: Microscope,
      label: 'Draft interview questions for this segment',
      prompt:
        'Turn the jobs, pains, and gains in this customer profile into a list of interview questions I can use to validate these assumptions with real customers.',
      condition: 'populated',
    },
  ],
  'customer-persona': [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Find missing details in this persona',
      prompt:
        'Scan this persona for missing or thin fields \u2014 empty bio, missing interests, vague jobs-to-be-done \u2014 and suggest what to fill in.',
      condition: 'populated',
    },
    {
      id: 'profile-gaps',
      order: 2,
      icon: TextSearch,
      label: 'Compare this persona to my Customer Profile',
      prompt:
        "Compare this persona against the linked Customer Profile and flag where they contradict or don't align.",
      condition: 'populated',
    },
    {
      id: 'pitch-slide',
      order: 3,
      icon: Presentation,
      label: 'Summarize this persona for a pitch slide',
      prompt:
        'Condense the most important details from this persona into a short, presentation-ready summary I can drop into a deck or stakeholder doc.',
      condition: 'populated',
    },
  ],
  'product-strategy-canvas': [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Identify gaps in this Strategy Canvas',
      prompt:
        'Scan all levels of this strategy canvas \u2014 flag any missing or thin Strategic Themes, Product Goals, and Product Ideas, especially ones that don\u2019t connect to each other.',
      condition: 'populated',
    },
  ],
  'knowns-unknowns': [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Identify gaps in my Knowns and Unknowns',
      prompt:
        'Check if the context and goal are defined, flag if the board is heavily imbalanced, and highlight items missing a desirability/feasibility tag or impact level.',
      condition: 'populated',
    },
    {
      id: 'summarize',
      order: 2,
      icon: Presentation,
      label: 'Summarize my Knowns vs. Unknowns',
      prompt:
        'Map my knowns and unknowns against the product goal and highlight areas where I have neither facts nor assumptions \u2014 what am I missing entirely?',
      condition: 'populated',
    },
  ],
  'value-proposition-canvas': [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Identify gaps in this value proposition',
      prompt:
        'Check if the product, customer segment, and competing value propositions are all present, and flag anything that\u2019s empty, too vague, or doesn\u2019t form a coherent value proposition.',
      condition: 'populated',
    },
    {
      id: 'landing-headline',
      order: 2,
      icon: Presentation,
      label: 'Turn this into a landing page headline',
      prompt:
        'Condense my value proposition into 1\u20132 punchy headline options I can test or drop into a creative brief.',
      condition: 'populated',
    },
  ],
  'five-whys': [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Find weak links in this root cause analysis',
      prompt:
        'Check if the problem statement is specific enough, flag any steps where the causal link is weak, and highlight assumptions that lack evidence.',
      condition: 'populated',
    },
    {
      id: 'summarize',
      order: 2,
      icon: Presentation,
      label: 'Summarize my root cause analysis for sharing',
      prompt:
        'Condense the root problem and the full Why chain into a short, readable root cause summary I can paste into a doc or slide.',
      condition: 'populated',
    },
  ],
  'opportunity-solution-tree': [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Identify gaps in this Opportunity Solution Tree',
      prompt:
        'Check if the desired outcome is defined, flag opportunities with no linked experiments, and highlight any branch that stops at solutions without experiments.',
      condition: 'populated',
    },
    {
      id: 'summarize',
      order: 2,
      icon: Presentation,
      label: 'Summarize this Opportunity Solution Tree',
      prompt:
        'Condense the desired outcome, opportunities, and solutions into a readable narrative I can paste into a doc or slide.',
      condition: 'populated',
    },
  ],
  'user-story-map': [
    {
      id: 'check-journey',
      order: 1,
      icon: Route,
      label: 'Validate whether my activities reflect the full customer journey',
      prompt:
        'Compare the activities in this Story Map against the linked Customer Journey Map and flag anything missing or misaligned with what users actually care about.',
      condition: 'populated',
    },
    {
      id: 'summarize',
      order: 2,
      icon: Presentation,
      label: 'Summarize this Story Map for sharing',
      prompt:
        'Condense the user goal, backbone, and key stories from this map into a short summary I can paste into a doc or slide.',
      condition: 'populated',
    },
  ],
};
